/**
 * UI Controller for managing user interface interactions (migrated to equalizer feature)
 */

import type { AudioEngineInterface, EQProcessorInterface, DataManagerInterface, ConfigManagerInterface } from '../../types';

class UIController {
    audioEngine: AudioEngineInterface;
    eqProcessor: EQProcessorInterface;
    dataManager: DataManagerInterface;
    configManager: ConfigManagerInterface;
    appController: { saveConfiguration: () => void; loadConfiguration: (event?: Event) => void; resetResults: () => void; calculateEqualization: () => void } | null;
    isNodeEnv: boolean;

    constructor(audioEngine: AudioEngineInterface, eqProcessor: EQProcessorInterface, dataManager: DataManagerInterface, configManager: ConfigManagerInterface, appController: { saveConfiguration: () => void; loadConfiguration: (event?: Event) => void; resetResults: () => void; calculateEqualization: () => void } | null = null) {
        this.audioEngine = audioEngine;
        this.eqProcessor = eqProcessor;
        this.dataManager = dataManager;
        this.configManager = configManager;
        this.appController = appController;
        this.isNodeEnv = typeof window === 'undefined';
    }

    /**
     * Initialize UI controls and event listeners
     */
    initializeControls() {
        if (this.isNodeEnv) return; // Skip for Node.js testing

        this.initVolumeControls();
        this.initAudioControls();
        this.initDataControls();
    }

    /**
     * Initialize volume control sliders
     */
    initVolumeControls() {
        const noiseVolumeSlider = document.getElementById('noise-volume') as HTMLInputElement | null;
        const sineVolumeSlider = document.getElementById('sine-volume') as HTMLInputElement | null;
        const noiseVolumeLabel = document.getElementById('noise-volume-label') as HTMLElement | null;
        const sineVolumeLabel = document.getElementById('sine-volume-label') as HTMLElement | null;

        if (noiseVolumeSlider) {
            noiseVolumeSlider.addEventListener('input', () => {
                const valueStr = noiseVolumeSlider.value;
                const value = parseFloat(valueStr);
                if (noiseVolumeLabel) noiseVolumeLabel.textContent = `${value} dB`;
                this.audioEngine.setNoiseVolume(value);
            });
        }

        if (sineVolumeSlider) {
            sineVolumeSlider.addEventListener('input', () => {
                const valueStr = sineVolumeSlider.value;
                const value = parseFloat(valueStr);
                if (sineVolumeLabel) sineVolumeLabel.textContent = `${value} dB`;
                this.audioEngine.setSineVolume(value);
            });
        }
    }

    /**
     * Initialize audio control buttons
     */
    initAudioControls() {
        const startStopBtn = document.getElementById('start-stop-btn') as HTMLButtonElement | null;
        
        if (startStopBtn) {
            startStopBtn.addEventListener('click', () => {
                this.handleStartStopClick();
            });
        }
    }

    /**
     * Initialize data control buttons
     */
    initDataControls() {
        const saveBtn = document.getElementById('save-btn') as HTMLElement | null;
        const loadBtn = document.getElementById('load-btn-hidden') as HTMLInputElement | null;
        const resetBtn = document.getElementById('reset-btn') as HTMLButtonElement | null;
        const calculateEqBtn = document.getElementById('calculate-eq-btn') as HTMLButtonElement | null;

        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                this.handleSaveClick();
            });
        }

        if (loadBtn) {
            loadBtn.addEventListener('change', (event) => {
                this.handleLoadClick(event);
            });
        }

        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                this.handleResetClick();
            });
        }

        if (calculateEqBtn) {
            calculateEqBtn.addEventListener('click', () => {
                this.handleCalculateEqClick();
            });
        }
    }

    /**
     * Handle start/stop button click
     */
    handleStartStopClick() {
        const startStopBtn = document.getElementById('start-stop-btn');
        
        if (!this.audioEngine.getIsStarted()) {
            this.audioEngine.startAudio();
            if (startStopBtn) startStopBtn.textContent = 'Stop';
            
            // Enable controls
            this.toggleControlsState(false);
        } else {
            this.audioEngine.stopAudio();
            if (startStopBtn) startStopBtn.textContent = 'Start';
            
            // Disable controls
            this.toggleControlsState(true);
        }
    }

    /**
     * Toggle enabled/disabled state of controls
     */
    toggleControlsState(disabled: boolean) {
        const controls = [
            'sine-volume',
            'frequency-slider',
            'prev-freq-btn',
            'next-freq-btn'
        ];

        controls.forEach(id => {
            const element = document.getElementById(id) as HTMLButtonElement | HTMLInputElement | null;
            if (element) {
                (element as HTMLButtonElement | HTMLInputElement).disabled = disabled;
            }
        });
    }

    /**
     * Handle save button click
     */
    handleSaveClick() {
        if (this.appController) {
            this.appController.saveConfiguration();
        }
    }

    /**
     * Handle load button click
     */
    handleLoadClick(event: Event) {
        if (this.appController) {
            this.appController.loadConfiguration(event);
        }
    }

    /**
     * Handle reset button click
     */
    handleResetClick() {
        if (this.appController) {
            this.appController.resetResults();
        }
    }

    /**
     * Handle calculate EQ button click
     */
    handleCalculateEqClick() {
        if (this.appController) {
            this.appController.calculateEqualization();
        }
    }

    /**
     * Update frequency display
     */
    updateFrequencyDisplay(frequency: number) {
        const frequencyLabel = document.getElementById('frequency-label') as HTMLElement | null;
        if (frequencyLabel) {
            frequencyLabel.textContent = `${frequency} Hz`;
        }
    }

    /**
     * Update volume labels
     */
    updateVolumeLabels(noiseVolume: number, sineVolume: number) {
        const noiseVolumeLabel = document.getElementById('noise-volume-label') as HTMLElement | null;
        const sineVolumeLabel = document.getElementById('sine-volume-label') as HTMLElement | null;

        if (noiseVolumeLabel) {
            noiseVolumeLabel.textContent = `${noiseVolume} dB`;
        }

        if (sineVolumeLabel) {
            sineVolumeLabel.textContent = `${sineVolume} dB`;
        }
    }

    /**
     * Set the app controller reference (for dependency injection after creation)
     */
    setAppController(appController: { saveConfiguration: () => void; loadConfiguration: (event?: Event) => void; resetResults: () => void; calculateEqualization: () => void }) {
        this.appController = appController;
    }
}

// ES module export
export { UIController };
export default UIController;